import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
  makeStyles,
} from "@material-ui/core";
import QRCode from "qrcode.react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
      overflow: "hidden",
    },
  },
  dialogHeader: {
    background: "linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)",
    padding: theme.spacing(3, 4, 2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  dialogTitle: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: 0.5,
    marginBottom: theme.spacing(2),
  },
  stepper: {
    background: "transparent",
    padding: theme.spacing(0, 0, 1),
    width: "100%",
    "& .MuiStepIcon-root.MuiStepIcon-active": {
      color: "#4fc3f7",
    },
    "& .MuiStepIcon-root.MuiStepIcon-completed": {
      color: "#4caf50",
    },
    "& .MuiStepLabel-label": {
      color: "rgba(255,255,255,0.6)",
      fontSize: 11,
    },
    "& .MuiStepLabel-label.MuiStepLabel-active": {
      color: "#ffffff",
      fontWeight: 600,
    },
    "& .MuiStepLabel-label.MuiStepLabel-completed": {
      color: "rgba(255,255,255,0.8)",
    },
    "& .MuiStepConnector-line": {
      borderColor: "rgba(255,255,255,0.2)",
    },
  },
  dialogContent: {
    padding: 0,
    overflowX: "hidden",
  },
  stepWrapper: {
    animation: "$fadeSlideIn 0.35s ease",
  },
  "@keyframes fadeSlideIn": {
    from: {
      opacity: 0,
      transform: "translateY(16px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  // Step 0 — Welcome
  welcomeContainer: {
    display: "flex",
    alignItems: "stretch",
    minHeight: 380,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  welcomeLeft: {
    flex: "0 0 240px",
    background: "linear-gradient(160deg, #0f3460 0%, #065183 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  welcomeRight: {
    flex: 1,
    padding: theme.spacing(5, 4),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  welcomeTitle: {
    fontWeight: 800,
    fontSize: 22,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.3,
  },
  welcomeSubtitle: {
    fontSize: 15,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    lineHeight: 1.6,
  },
  welcomeFeatures: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    marginBottom: theme.spacing(4),
    "& li": {
      fontSize: 14,
      color: theme.palette.text.secondary,
      padding: theme.spacing(0.5, 0),
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
  },
  // Generic step container
  stepContainer: {
    padding: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2.5),
    },
  },
  stepCard: {
    borderRadius: 12,
    padding: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "1px solid",
    borderColor: theme.palette.divider,
    background: theme.palette.background.paper,
  },
  stepTitle: {
    fontWeight: 700,
    fontSize: 18,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  stepSubtitle: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  // User rows
  userRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexWrap: "wrap",
    },
  },
  userRowField: {
    flex: 1,
    minWidth: 100,
    [theme.breakpoints.down("xs")]: {
      minWidth: "calc(50% - 6px)",
    },
  },
  userRowError: {
    fontSize: 11,
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0, 1),
  },
  addUserBtn: {
    marginTop: theme.spacing(1),
    borderStyle: "dashed",
    color: "#065183",
    borderColor: "#065183",
    "&:hover": {
      background: "rgba(6,81,131,0.05)",
      borderStyle: "dashed",
    },
  },
  // Color field
  colorRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  colorLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    minWidth: 80,
  },
  colorInputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: 8,
    background: theme.palette.background.default,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#065183",
    },
  },
  colorSwatch: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "2px solid rgba(0,0,0,0.12)",
    flexShrink: 0,
  },
  colorInput: {
    opacity: 0,
    position: "absolute",
    width: 0,
    height: 0,
    pointerEvents: "none",
  },
  colorValue: {
    fontSize: 13,
    fontFamily: "monospace",
    color: theme.palette.text.primary,
    letterSpacing: 1,
  },
  // WhatsApp step
  whatsappContainer: {
    display: "flex",
    gap: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  whatsappLeft: {
    flex: "0 0 180px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  whatsappRight: {
    flex: 1,
  },
  successBox: {
    background: "linear-gradient(135deg, rgba(76,175,80,0.08) 0%, rgba(6,81,131,0.06) 100%)",
    border: "1px solid rgba(76,175,80,0.3)",
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: "center",
    marginTop: theme.spacing(2),
  },
  successIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  successTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: "#2e7d32",
    marginBottom: theme.spacing(1),
  },
  successText: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  // Done step
  doneContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(5, 4),
    textAlign: "center",
    minHeight: 380,
    justifyContent: "center",
  },
  doneTitle: {
    fontWeight: 800,
    fontSize: 24,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  doneSummaryCard: {
    background: theme.palette.background.default,
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: 12,
    padding: theme.spacing(2.5, 3),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
    width: "100%",
    maxWidth: 400,
    textAlign: "left",
  },
  doneSummaryRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 0),
    fontSize: 14,
    color: theme.palette.text.primary,
    borderBottom: "1px solid",
    borderColor: theme.palette.divider,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  doneSummaryLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    flex: "0 0 120px",
  },
  doneSummaryValue: {
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  // Action bar
  actionBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2.5, 4),
    borderTop: "1px solid",
    borderColor: theme.palette.divider,
    background: theme.palette.background.paper,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  actionBarRight: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
  },
  primaryBtn: {
    background: "linear-gradient(135deg, #065183 0%, #0f3460 100%)",
    color: "#ffffff",
    fontWeight: 700,
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    textTransform: "none",
    fontSize: 14,
    "&:hover": {
      background: "linear-gradient(135deg, #054470 0%, #0a2a50 100%)",
    },
    "&:disabled": {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  secondaryBtn: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    borderRadius: 8,
    padding: theme.spacing(1, 2.5),
    textTransform: "none",
    fontSize: 14,
  },
  skipBtn: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: 13,
    textTransform: "none",
    padding: theme.spacing(0.5, 1),
    "&:hover": {
      background: "transparent",
      textDecoration: "underline",
    },
  },
  // Robot animations
  "@keyframes floatRobot": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-10px)" },
  },
  "@keyframes celebrate": {
    "0%, 100%": { transform: "rotate(0deg) scale(1)" },
    "25%": { transform: "rotate(-5deg) scale(1.04)" },
    "75%": { transform: "rotate(5deg) scale(1.04)" },
  },
  robotFloat: {
    animation: "$floatRobot 3s ease-in-out infinite",
    display: "block",
  },
  robotCelebrate: {
    animation: "$celebrate 1.2s ease-in-out infinite",
    display: "block",
  },
}));

// ─── Inline Robot SVG ─────────────────────────────────────────────────────────

function RobotMascot({ variant = "float", size = 140, inverted = false }) {
  const classes = useStyles();
  const bodyFill = inverted ? "#ffffff" : "#065183";
  const eyeFill = inverted ? "#065183" : "#ffffff";
  const glowColor = "#4fc3f7";
  const mouthFill = inverted ? "rgba(6,81,131,0.7)" : "rgba(255,255,255,0.8)";
  const antennaColor = inverted ? "#ffffff" : "#065183";

  const animClass = variant === "celebrate" ? classes.robotCelebrate : classes.robotFloat;

  return (
    <svg
      className={animClass}
      width={size}
      height={size}
      viewBox="0 0 100 110"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Antenna line */}
      <line x1="50" y1="5" x2="50" y2="18" stroke={antennaColor} strokeWidth="2.5" strokeLinecap="round" />
      {/* Antenna ball */}
      <circle cx="50" cy="4" r="4" fill={glowColor} />
      <circle cx="50" cy="4" r="2" fill="#ffffff" />

      {/* Left ear */}
      <rect x="6" y="30" width="8" height="14" rx="4" fill={bodyFill} />
      {/* Right ear */}
      <rect x="86" y="30" width="8" height="14" rx="4" fill={bodyFill} />

      {/* Head body */}
      <rect x="16" y="18" width="68" height="58" rx="14" fill={bodyFill} />

      {/* Left eye outer glow */}
      <circle cx="36" cy="40" r="12" fill="none" stroke={glowColor} strokeWidth="1.5" opacity="0.4" />
      {/* Left eye white */}
      <circle cx="36" cy="40" r="9" fill={eyeFill} />
      {/* Left eye pupil */}
      <circle cx="36" cy="40" r="4" fill={inverted ? "#ffffff" : "#065183"} />
      {/* Left eye shine */}
      <circle cx="39" cy="37" r="2" fill={inverted ? "#065183" : "#ffffff"} opacity="0.9" />

      {/* Right eye outer glow */}
      <circle cx="64" cy="40" r="12" fill="none" stroke={glowColor} strokeWidth="1.5" opacity="0.4" />
      {/* Right eye white */}
      <circle cx="64" cy="40" r="9" fill={eyeFill} />
      {/* Right eye pupil */}
      <circle cx="64" cy="40" r="4" fill={inverted ? "#ffffff" : "#065183"} />
      {/* Right eye shine */}
      <circle cx="67" cy="37" r="2" fill={inverted ? "#065183" : "#ffffff"} opacity="0.9" />

      {/* Mouth */}
      <rect x="33" y="60" width="34" height="8" rx="4" fill={mouthFill} />
      {/* Mouth highlight dots */}
      <circle cx="40" cy="64" r="2" fill={inverted ? "rgba(6,81,131,0.5)" : "rgba(255,255,255,0.5)"} />
      <circle cx="50" cy="64" r="2" fill={inverted ? "rgba(6,81,131,0.5)" : "rgba(255,255,255,0.5)"} />
      <circle cx="60" cy="64" r="2" fill={inverted ? "rgba(6,81,131,0.5)" : "rgba(255,255,255,0.5)"} />

      {/* Body / torso */}
      <rect x="24" y="80" width="52" height="26" rx="8" fill={bodyFill} opacity="0.7" />

      {/* Chest panel */}
      <rect x="34" y="86" width="32" height="14" rx="4" fill={inverted ? "rgba(6,81,131,0.15)" : "rgba(255,255,255,0.15)"} />
      {/* Chest lights */}
      <circle cx="41" cy="93" r="2.5" fill={glowColor} opacity="0.9" />
      <circle cx="50" cy="93" r="2.5" fill={inverted ? "#065183" : "#ffffff"} opacity="0.7" />
      <circle cx="59" cy="93" r="2.5" fill={glowColor} opacity="0.9" />
    </svg>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const ADMIN_STEPS = [
  "Bem-vindo",
  "Usuários",
  "Fila",
  "WhatsApp",
  "Visibilidade",
  "Concluído",
];

const USER_STEPS = [
  "Bem-vindo",
  "Suas Filas",
  "Pronto!",
];

const EMPTY_USER = { name: "", email: "", password: "", profile: "user" };

export default function SetupWizard() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [activeStep, setActiveStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Step 1 state
  const [userRows, setUserRows] = useState([{ ...EMPTY_USER }]);
  const [userErrors, setUserErrors] = useState([]);

  // Step 2 state
  const [queueName, setQueueName] = useState("");
  // Gera cor aleatória agradável para a fila (paleta de cores vivas)
  const [queueColor] = useState(() => {
    const palette = [
      "#065183", "#0ea5e9", "#6366f1", "#8b5cf6", "#ec4899",
      "#f59e0b", "#10b981", "#ef4444", "#14b8a6", "#f97316",
    ];
    return palette[Math.floor(Math.random() * palette.length)];
  });
  const [queueGreeting, setQueueGreeting] = useState("");
  const [queueNameError, setQueueNameError] = useState("");
  const [savedQueueId, setSavedQueueId] = useState(null);
  const [savedQueueName, setSavedQueueName] = useState("");

  // Step 3 state
  const [whatsappName, setWhatsappName] = useState("Meu WhatsApp");
  const [whatsappNameError, setWhatsappNameError] = useState("");
  const [connectionCreated, setConnectionCreated] = useState(false);
  const [savedConnectionName, setSavedConnectionName] = useState("");
  const [savedWhatsappId, setSavedWhatsappId] = useState(null);
  const [wizardQrCode, setWizardQrCode] = useState("");
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const qrPollRef = useRef(null);

  // Summary
  const [createdUsersCount, setCreatedUsersCount] = useState(0);

  // Step 4 state — visibilidade de atendimentos
  const [viewTicketsAllAttendants, setViewTicketsAllAttendants] = useState("enabled");

  // ── User-wizard state ─────────────────────────────────────────────────────
  const [companyQueues, setCompanyQueues] = useState([]);
  const [userQueueIds, setUserQueueIds] = useState([]);
  const [loadingQueues, setLoadingQueues] = useState(false);

  // Ref para input de cor (deve ser declarado antes de qualquer return condicional)
  const colorInputRef = useRef(null);

  // ── Determine wizard mode e visibilidade ──────────────────────────────────
  useEffect(() => {
    if (!user) return;
    if (user.companyId === 1) return;

    const isAdminUser = user.profile === "admin" || user.super === true;
    // Chave diferente para admin (empresa) vs usuário (pessoal)
    const settingKey = isAdminUser
      ? "setupWizardDone"
      : `userWizardDone_${user.id}`;

    api.get(`/settings/${settingKey}`)
      .then(({ data }) => {
        const isDone = data && data.value === "true";
        if (!isDone) setOpen(true);
      })
      .catch(() => {
        setOpen(true);
      });
  }, [user]);

  // Carrega filas quando o wizard de usuário estiver aberto no step 1
  useEffect(() => {
    if (!open) return;
    if (!user) return;
    const isAdminUser = user.profile === "admin" || user.super === true;
    if (isAdminUser) return; // só no modo usuário

    setLoadingQueues(true);
    Promise.all([
      api.get("/queue"),
      api.get(`/users/${user.id}`),
    ])
      .then(([queuesRes, userRes]) => {
        setCompanyQueues(queuesRes.data?.queues || queuesRes.data || []);
        const assignedIds = (userRes.data?.queues || []).map((q) => q.id);
        setUserQueueIds(assignedIds);
      })
      .catch(() => {})
      .finally(() => setLoadingQueues(false));
  }, [open, user]);

  // ── QR Code: poll inicial + escuta socket ─────────────────────────────────
  useEffect(() => {
    if (!savedWhatsappId || whatsappConnected) return;

    const fetchQr = async () => {
      try {
        const { data } = await api.get(`/whatsapp/${savedWhatsappId}`);
        if (data.qrcode) setWizardQrCode(data.qrcode);
        if (data.status === "CONNECTED") {
          setWhatsappConnected(true);
          setWizardQrCode("");
        }
      } catch (_) {}
    };

    fetchQr();
    qrPollRef.current = setInterval(fetchQr, 15000);
    return () => clearInterval(qrPollRef.current);
  }, [savedWhatsappId, whatsappConnected]);

  useEffect(() => {
    if (!savedWhatsappId || !user) return;
    const companyId = user.companyId;

    const onWhatsappSession = (data) => {
      if (data.session?.id !== savedWhatsappId) return;
      if (data.action === "update") {
        if (data.session.qrcode) setWizardQrCode(data.session.qrcode);
        if (data.session.status === "CONNECTED" || data.session.qrcode === "") {
          setWhatsappConnected(true);
          setWizardQrCode("");
          clearInterval(qrPollRef.current);
        }
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, onWhatsappSession);
    return () => socket.off(`company-${companyId}-whatsappSession`, onWhatsappSession);
  }, [savedWhatsappId, user, socket]);

  if (!user) return null;
  if (user.companyId === 1) return null;

  const isAdminWizard = user.profile === "admin" || user.super === true;
  const STEPS = isAdminWizard ? ADMIN_STEPS : USER_STEPS;

  // ── Helpers ───────────────────────────────────────────────────────────────

  const markDone = async () => {
    const settingKey = isAdminWizard
      ? "setupWizardDone"
      : `userWizardDone_${user.id}`;
    try {
      await api.put(`/settings/${settingKey}`, { value: "true" });
    } catch (err) {
      // silencioso
    }
  };

  const handleClose = () => {
    markDone();
    setOpen(false);
  };

  // ── USER WIZARD renders ───────────────────────────────────────────────────

  const renderUserStep0 = () => (
    <div className={classes.welcomeContainer}>
      <div className={classes.welcomeLeft}>
        <RobotMascot variant="float" size={160} inverted />
      </div>
      <div className={classes.welcomeRight}>
        <Typography className={classes.welcomeTitle}>
          Olá, {user.name?.split(" ")[0]}! 👋
        </Typography>
        <Typography className={classes.welcomeSubtitle}>
          Seja bem-vindo(a) ao Chatbot GoldTarget. Este assistente vai mostrar como a plataforma funciona para você começar a atender com confiança.
        </Typography>
        <ul className={classes.welcomeFeatures}>
          <li><span>📋</span> Veja as filas de atendimento da sua empresa</li>
          <li><span>💬</span> Entenda como receber tickets</li>
          <li><span>🚀</span> Comece a atender em minutos</li>
        </ul>
      </div>
    </div>
  );

  const renderUserStep1 = () => (
    <div style={{ padding: "8px 0" }}>
      <Typography className={classes.stepTitle}>
        📋 Filas de Atendimento
      </Typography>
      <Typography className={classes.stepSubtitle}>
        Estas são as filas cadastradas na sua empresa. Você receberá tickets das filas em que estiver vinculado. Se precisar de acesso a mais filas, fale com um administrador.
      </Typography>

      {loadingQueues ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <CircularProgress size={32} style={{ color: "#065183" }} />
          <Typography style={{ marginTop: 12, color: "#64748b", fontSize: 14 }}>
            Carregando filas...
          </Typography>
        </div>
      ) : companyQueues.length === 0 ? (
        <Paper elevation={0} style={{
          padding: 24, textAlign: "center", background: "#f8fafc",
          borderRadius: 12, border: "1px dashed #cbd5e1"
        }}>
          <Typography style={{ color: "#94a3b8", fontSize: 14 }}>
            Nenhuma fila cadastrada ainda. O administrador irá configurar as filas em breve.
          </Typography>
        </Paper>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
          {companyQueues.map((queue) => {
            const isAssigned = userQueueIds.includes(queue.id);
            return (
              <Paper key={queue.id} elevation={0} style={{
                display: "flex", alignItems: "center", gap: 14, padding: "14px 18px",
                borderRadius: 12, border: `1.5px solid ${isAssigned ? queue.color || "#065183" : "#e2e8f0"}`,
                background: isAssigned ? `${queue.color || "#065183"}0d` : "#fff",
                transition: "all 0.2s",
              }}>
                <div style={{
                  width: 14, height: 14, borderRadius: "50%", flexShrink: 0,
                  background: queue.color || "#065183",
                  boxShadow: `0 0 0 3px ${queue.color || "#065183"}30`,
                }} />
                <div style={{ flex: 1 }}>
                  <Typography style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>
                    {queue.name}
                  </Typography>
                  {queue.greetingMessage && (
                    <Typography style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                      {queue.greetingMessage.length > 80
                        ? queue.greetingMessage.slice(0, 80) + "..."
                        : queue.greetingMessage}
                    </Typography>
                  )}
                </div>
                <div style={{
                  padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: isAssigned ? (queue.color || "#065183") : "#f1f5f9",
                  color: isAssigned ? "#fff" : "#94a3b8",
                }}>
                  {isAssigned ? "✓ Vinculado" : "Não vinculado"}
                </div>
              </Paper>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderUserStep2 = () => (
    <div className={classes.doneContainer}>
      <RobotMascot variant="celebrate" size={160} />
      <Typography className={classes.doneTitle} style={{ marginTop: 16 }}>
        Tudo pronto! 🎉
      </Typography>
      <Typography style={{ color: "#64748b", fontSize: 14, marginBottom: 24, textAlign: "center", maxWidth: 420 }}>
        Agora você já conhece as filas da sua empresa. Quando chegarem novos tickets, você vai recebê-los aqui mesmo. Bom atendimento!
      </Typography>
      <Button className={classes.primaryBtn} onClick={handleFinish} size="large">
        Ir para o Dashboard
      </Button>
    </div>
  );

  // ── Step 0: Welcome ───────────────────────────────────────────────────────

  const handleStart = () => {
    setActiveStep(1);
  };

  // ── Step 1: Users ─────────────────────────────────────────────────────────

  const handleAddUserRow = () => {
    setUserRows((prev) => [...prev, { ...EMPTY_USER }]);
  };

  const handleRemoveUserRow = (idx) => {
    setUserRows((prev) => prev.filter((_, i) => i !== idx));
    setUserErrors((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUserRowChange = (idx, field, value) => {
    setUserRows((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );
    setUserErrors((prev) =>
      prev.map((err, i) => (i === idx ? null : err))
    );
  };

  const handleUsersNext = async () => {
    const nonEmpty = userRows.filter(
      (r) => r.name.trim() || r.email.trim() || r.password.trim()
    );

    if (nonEmpty.length === 0) {
      setActiveStep(2);
      return;
    }

    setLoading(true);
    const errors = new Array(userRows.length).fill(null);
    let successCount = 0;

    for (let i = 0; i < userRows.length; i++) {
      const row = userRows[i];
      if (!row.name.trim() && !row.email.trim() && !row.password.trim()) continue;

      try {
        await api.post("/users", {
          name: row.name.trim(),
          email: row.email.trim(),
          password: row.password,
          profile: row.profile,
        });
        successCount++;
      } catch (err) {
        const msg =
          err?.response?.data?.error || err?.response?.data?.message || "Erro ao criar usuário";
        errors[i] = msg;
      }
    }

    setUserErrors(errors);
    setLoading(false);

    const hasErrors = errors.some(Boolean);

    if (successCount > 0) {
      setCreatedUsersCount((prev) => prev + successCount);
      toast.success(`${successCount} usuário(s) criado(s) com sucesso!`);
    }

    if (!hasErrors) {
      setActiveStep(2);
    }
  };

  const handleSkipUsers = () => {
    setActiveStep(2);
  };

  // ── Step 2: Queue ─────────────────────────────────────────────────────────

  const handleQueueNext = async () => {
    if (!queueName.trim()) {
      setQueueNameError("Nome da fila é obrigatório");
      return;
    }
    setQueueNameError("");
    setLoading(true);

    try {
      const { data } = await api.post("/queue", {
        name: queueName.trim(),
        color: queueColor,
        greetingMessage: queueGreeting.trim(),
      });
      setSavedQueueId(data.id);
      setSavedQueueName(data.name || queueName.trim());
      toast.success("Fila criada com sucesso!");
      setActiveStep(3);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: WhatsApp ──────────────────────────────────────────────────────

  const handleCreateConnection = async () => {
    if (!whatsappName.trim()) {
      setWhatsappNameError("Nome da conexão é obrigatório");
      return;
    }
    setWhatsappNameError("");
    setLoading(true);

    try {
      const payload = {
        name: whatsappName.trim(),
        isDefault: true,
      };
      if (savedQueueId) {
        payload.queueIds = [savedQueueId];
      }
      const { data: wpData } = await api.post("/whatsapp", payload);
      setSavedConnectionName(whatsappName.trim());
      setSavedWhatsappId(wpData.id);
      setConnectionCreated(true);
      toast.success("Conexão criada! Escaneie o QR Code abaixo.");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipWhatsapp = () => {
    setActiveStep(4);
  };

  const handleWhatsappNext = () => {
    setActiveStep(4);
  };

  // ── Step 4: Visibilidade ──────────────────────────────────────────────────

  const handleVisibilityNext = async () => {
    setLoading(true);
    try {
      await api.put("/companySettings/", {
        column: "viewTicketsAllAttendants",
        data: viewTicketsAllAttendants,
      });
      setActiveStep(5);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 5: Done ──────────────────────────────────────────────────────────

  const handleFinish = () => {
    handleClose();
    history.push("/");
  };

  // colorInputRef moved to top of hooks (before early returns)

  // ── Render steps ─────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <div className={classes.welcomeContainer}>
      <div className={classes.welcomeLeft}>
        <RobotMascot variant="float" size={160} inverted />
      </div>
      <div className={classes.welcomeRight}>
        <Typography className={classes.welcomeTitle}>
          Bem-vindo ao Chatbot GoldTarget! 🤖
        </Typography>
        <Typography className={classes.welcomeSubtitle}>
          Vamos configurar sua conta em poucos minutos. Este assistente vai guiá-lo pelos passos essenciais para começar a atender seus clientes.
        </Typography>
        <ul className={classes.welcomeFeatures}>
          <li><span>👤</span> Criar usuários da sua equipe</li>
          <li><span>📋</span> Configurar sua primeira fila de atendimento</li>
          <li><span>📱</span> Conectar seu WhatsApp</li>
        </ul>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className={classes.stepContainer}>
      <div className={classes.stepCard}>
        <Typography className={classes.stepTitle}>Criar Usuários</Typography>
        <Typography className={classes.stepSubtitle}>
          Adicione os membros da sua equipe. Esta etapa é opcional — você pode criar usuários depois em Configurações.
        </Typography>

        {userRows.map((row, idx) => (
          <div key={idx}>
            <div className={classes.userRow}>
              <TextField
                className={classes.userRowField}
                label="Nome"
                size="small"
                variant="outlined"
                value={row.name}
                onChange={(e) => handleUserRowChange(idx, "name", e.target.value)}
              />
              <TextField
                className={classes.userRowField}
                label="E-mail"
                size="small"
                variant="outlined"
                value={row.email}
                onChange={(e) => handleUserRowChange(idx, "email", e.target.value)}
              />
              <TextField
                className={classes.userRowField}
                label="Senha"
                size="small"
                variant="outlined"
                type="password"
                value={row.password}
                onChange={(e) => handleUserRowChange(idx, "password", e.target.value)}
              />
              <FormControl size="small" variant="outlined" style={{ minWidth: 100 }}>
                <InputLabel>Perfil</InputLabel>
                <Select
                  value={row.profile}
                  label="Perfil"
                  onChange={(e) => handleUserRowChange(idx, "profile", e.target.value)}
                >
                  <MenuItem value="user">Usuário</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => handleRemoveUserRow(idx)}
                title="Remover"
                style={{ marginTop: 4, color: "#e53935" }}
              >
                <span style={{ fontSize: 18, lineHeight: 1 }}>×</span>
              </IconButton>
            </div>
            {userErrors[idx] && (
              <Typography className={classes.userRowError}>
                ⚠ {userErrors[idx]}
              </Typography>
            )}
          </div>
        ))}

        <Button
          variant="outlined"
          className={classes.addUserBtn}
          onClick={handleAddUserRow}
          size="small"
        >
          ＋ Adicionar usuário
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className={classes.stepContainer}>
      <div className={classes.stepCard}>
        <Typography className={classes.stepTitle}>Configurar Fila</Typography>
        <Typography className={classes.stepSubtitle}>
          Configure sua primeira fila de atendimento. Ela será usada para organizar as conversas da sua equipe.
        </Typography>

        <TextField
          label="Nome da fila"
          variant="outlined"
          fullWidth
          required
          value={queueName}
          onChange={(e) => {
            setQueueName(e.target.value);
            if (e.target.value.trim()) setQueueNameError("");
          }}
          error={Boolean(queueNameError)}
          helperText={queueNameError}
          style={{ marginBottom: 16 }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", background: queueColor, flexShrink: 0,
            boxShadow: `0 0 0 4px ${queueColor}30`,
          }} />
          <Typography style={{ fontSize: 13, color: "#64748b" }}>
            Cor gerada automaticamente
          </Typography>
        </div>

        <TextField
          label="Mensagem de saudação"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={queueGreeting}
          onChange={(e) => setQueueGreeting(e.target.value)}
          placeholder="Ex: Olá! Como posso ajudar você hoje?"
          style={{ marginTop: 8 }}
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className={classes.stepContainer}>
      <div className={classes.whatsappContainer}>
        <div className={classes.whatsappLeft}>
          <RobotMascot variant="float" size={150} />
        </div>
        <div className={classes.whatsappRight}>
          <Typography className={classes.stepTitle}>Conectar WhatsApp</Typography>
          <Typography className={classes.stepSubtitle}>
            {!connectionCreated
              ? "Crie sua conexão com o WhatsApp e escaneie o QR Code aqui mesmo."
              : whatsappConnected
              ? "WhatsApp conectado com sucesso!"
              : "Abra o WhatsApp no celular → Menu → Aparelhos conectados → Conectar aparelho."}
          </Typography>

          {!connectionCreated ? (
            <>
              <TextField
                label="Nome da conexão"
                variant="outlined"
                fullWidth
                required
                value={whatsappName}
                onChange={(e) => {
                  setWhatsappName(e.target.value);
                  if (e.target.value.trim()) setWhatsappNameError("");
                }}
                error={Boolean(whatsappNameError)}
                helperText={whatsappNameError}
                style={{ marginBottom: 16 }}
              />
              {savedQueueName && (
                <Typography style={{ fontSize: 12, color: "#065183", marginBottom: 8 }}>
                  📋 Fila vinculada: <strong>{savedQueueName}</strong>
                </Typography>
              )}
              <Button
                className={classes.primaryBtn}
                onClick={handleCreateConnection}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {loading ? "Criando..." : "Criar conexão"}
              </Button>
            </>
          ) : whatsappConnected ? (
            <div className={classes.successBox}>
              <div className={classes.successIcon}>✅</div>
              <Typography className={classes.successTitle}>WhatsApp conectado!</Typography>
              <Typography className={classes.successText}>
                Seu número está ativo e pronto para receber atendimentos.
              </Typography>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              {wizardQrCode ? (
                <>
                  <div style={{
                    background: "#fff",
                    padding: 16,
                    borderRadius: 12,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                    display: "inline-block",
                  }}>
                    <QRCode value={wizardQrCode} size={200} />
                  </div>
                  <Typography style={{ fontSize: 12, color: "#64748b", textAlign: "center" }}>
                    O QR Code é renovado automaticamente. Aguarde se expirar.
                  </Typography>
                </>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "20px 0" }}>
                  <CircularProgress size={36} style={{ color: "#065183" }} />
                  <Typography style={{ fontSize: 13, color: "#64748b" }}>
                    Aguardando QR Code...
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const optionStyle = (value) => ({
      flex: 1,
      minWidth: 200,
      padding: "20px 16px",
      borderRadius: 12,
      border: `2px solid ${viewTicketsAllAttendants === value ? "#065183" : "#e2e8f0"}`,
      background: viewTicketsAllAttendants === value ? "rgba(6,81,131,0.07)" : "#fff",
      cursor: "pointer",
      transition: "all 0.2s",
      textAlign: "left",
    });
    return (
      <div className={classes.stepContainer}>
        <div className={classes.stepCard}>
          <Typography className={classes.stepTitle}>Visibilidade de Atendimentos</Typography>
          <Typography className={classes.stepSubtitle}>
            Os atendentes podem ver os atendimentos de outros atendentes? Ou apenas os próprios?
          </Typography>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8 }}>
            <div style={optionStyle("enabled")} onClick={() => setViewTicketsAllAttendants("enabled")}>
              <Typography style={{ fontWeight: 700, fontSize: 15, color: "#1e293b", marginBottom: 4 }}>
                ✅ Sim, todos veem
              </Typography>
              <Typography style={{ fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
                Todos os atendentes enxergam os atendimentos uns dos outros. Recomendado para equipes pequenas.
              </Typography>
            </div>
            <div style={optionStyle("disabled")} onClick={() => setViewTicketsAllAttendants("disabled")}>
              <Typography style={{ fontWeight: 700, fontSize: 15, color: "#1e293b", marginBottom: 4 }}>
                🔒 Não, apenas o próprio
              </Typography>
              <Typography style={{ fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
                Cada atendente vê somente seus próprios atendimentos. Administradores continuam vendo todos.
              </Typography>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStep5 = () => (
    <div className={classes.doneContainer}>
      <RobotMascot variant="celebrate" size={160} />
      <Typography className={classes.doneTitle} style={{ marginTop: 16 }}>
        Configuração concluída! 🎉
      </Typography>
      <Typography style={{ color: theme.palette.text.secondary, fontSize: 14, marginBottom: 8 }}>
        Seu chatbot está pronto para atender. Aqui está um resumo do que foi configurado:
      </Typography>

      <Paper className={classes.doneSummaryCard} elevation={0}>
        <div className={classes.doneSummaryRow}>
          <span className={classes.doneSummaryLabel}>👤 Usuários criados</span>
          <span className={classes.doneSummaryValue}>
            {createdUsersCount > 0 ? `${createdUsersCount} usuário(s)` : "Nenhum"}
          </span>
        </div>
        <div className={classes.doneSummaryRow}>
          <span className={classes.doneSummaryLabel}>📋 Fila criada</span>
          <span className={classes.doneSummaryValue}>
            {savedQueueName || "Nenhuma"}
          </span>
        </div>
        <div className={classes.doneSummaryRow}>
          <span className={classes.doneSummaryLabel}>📱 Conexão criada</span>
          <span className={classes.doneSummaryValue}>
            {savedConnectionName || "Nenhuma"}
          </span>
        </div>
        <div className={classes.doneSummaryRow}>
          <span className={classes.doneSummaryLabel}>👁 Visibilidade</span>
          <span className={classes.doneSummaryValue}>
            {viewTicketsAllAttendants === "disabled" ? "Apenas o próprio atendente" : "Todos os atendentes"}
          </span>
        </div>
      </Paper>

      <Button
        className={classes.primaryBtn}
        onClick={handleFinish}
        size="large"
      >
        Ir para o Dashboard
      </Button>
    </div>
  );

  const stepContent = isAdminWizard
    ? [renderStep0(), renderStep1(), renderStep2(), renderStep3(), renderStep4(), renderStep5()]
    : [renderUserStep0(), renderUserStep1(), renderUserStep2()];

  // ── Action bar ────────────────────────────────────────────────────────────

  const renderActionBar = () => {
    // ── Modo usuário (3 passos) ───────────────────────────────────────────
    if (!isAdminWizard) {
      if (activeStep === 0) {
        return (
          <div className={classes.actionBar}>
            <span />
            <div className={classes.actionBarRight}>
              <Button className={classes.primaryBtn} onClick={handleStart}>
                Ver minhas filas →
              </Button>
            </div>
          </div>
        );
      }
      if (activeStep === 1) {
        return (
          <div className={classes.actionBar}>
            <Button className={classes.skipBtn} onClick={() => setActiveStep(2)} disableRipple>
              Pular →
            </Button>
            <div className={classes.actionBarRight}>
              <Button className={classes.secondaryBtn} onClick={() => setActiveStep(0)}>
                Voltar
              </Button>
              <Button className={classes.primaryBtn} onClick={() => setActiveStep(2)}>
                Entendi, continuar →
              </Button>
            </div>
          </div>
        );
      }
      // step 2 (done) não tem action bar — botão está inline
      return null;
    }

    // ── Modo admin (5 passos) ─────────────────────────────────────────────
    if (activeStep === 0) {
      return (
        <div className={classes.actionBar}>
          <span />
          <div className={classes.actionBarRight}>
            <Button className={classes.primaryBtn} onClick={handleStart}>
              Começar →
            </Button>
          </div>
        </div>
      );
    }

    if (activeStep === 1) {
      return (
        <div className={classes.actionBar}>
          <Button className={classes.skipBtn} onClick={handleSkipUsers} disableRipple>
            Pular esta etapa →
          </Button>
          <div className={classes.actionBarRight}>
            <Button className={classes.secondaryBtn} onClick={() => setActiveStep(0)}>
              Voltar
            </Button>
            <Button
              className={classes.primaryBtn}
              onClick={handleUsersNext}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading ? "Salvando..." : "Próximo"}
            </Button>
          </div>
        </div>
      );
    }

    if (activeStep === 2) {
      return (
        <div className={classes.actionBar}>
          <span />
          <div className={classes.actionBarRight}>
            <Button className={classes.secondaryBtn} onClick={() => setActiveStep(1)}>
              Voltar
            </Button>
            <Button
              className={classes.primaryBtn}
              onClick={handleQueueNext}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading ? "Criando fila..." : "Próximo"}
            </Button>
          </div>
        </div>
      );
    }

    if (activeStep === 3) {
      return (
        <div className={classes.actionBar}>
          <Button className={classes.skipBtn} onClick={handleSkipWhatsapp} disableRipple>
            Pular esta etapa →
          </Button>
          <div className={classes.actionBarRight}>
            <Button className={classes.secondaryBtn} onClick={() => setActiveStep(2)}>
              Voltar
            </Button>
            {(connectionCreated) && (
              <Button className={classes.primaryBtn} onClick={handleWhatsappNext}>
                {whatsappConnected ? "Próximo →" : "Pular QR Code →"}
              </Button>
            )}
          </div>
        </div>
      );
    }

    if (activeStep === 4) {
      return (
        <div className={classes.actionBar}>
          <span />
          <div className={classes.actionBarRight}>
            <Button className={classes.secondaryBtn} onClick={() => setActiveStep(3)}>
              Voltar
            </Button>
            <Button
              className={classes.primaryBtn}
              onClick={handleVisibilityNext}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading ? "Salvando..." : "Concluir →"}
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
      disableBackdropClick
      disableEscapeKeyDown
    >
      {/* Header with gradient + stepper */}
      <div className={classes.dialogHeader}>
        <Typography className={classes.dialogTitle}>
          {isAdminWizard ? "Assistente de Configuração" : "Bem-vindo à equipe! 🤖"}
        </Typography>
        <Stepper
          activeStep={activeStep}
          className={classes.stepper}
          alternativeLabel={!isMobile}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>

      {/* Step content */}
      <DialogContent className={classes.dialogContent}>
        <div key={activeStep} className={classes.stepWrapper}>
          {stepContent[activeStep]}
        </div>
      </DialogContent>

      {/* Action bar */}
      {renderActionBar()}
    </Dialog>
  );
}
